<template>
  <div class="relative">
    <div v-if="submitting" class="absolute -inset-6 z-30 flex flex-col items-center justify-center rounded-2xl bg-white/95 px-8 text-center backdrop-blur-sm" role="status" aria-live="assertive">
      <span class="h-11 w-11 animate-spin rounded-full border-4 border-blue-100 border-t-[#2D8CFF]" />
      <p class="mt-5 text-lg font-semibold text-zinc-900">Securing your payment method…</p>
      <p class="mt-2 max-w-sm text-sm leading-6 text-zinc-500">Please keep this window open while Bank of America securely verifies and saves your card.</p>
    </div>
    <div class="flex items-start justify-between gap-4">
      <div>
        <h2 class="text-lg font-semibold text-zinc-900">Update payment method</h2>
        <p class="mt-1 text-sm text-zinc-500">Your card details are encrypted and your full card number is never stored by Platforma.</p>
      </div>
      <button type="button" class="rounded-lg p-2 text-zinc-400 transition hover:bg-zinc-100 hover:text-zinc-700" @click="$emit('close')">
        <XMarkIcon class="h-5 w-5" />
        <span class="sr-only">Close</span>
      </button>
    </div>

    <div class="mt-6 space-y-4">
      <label class="block">
        <span class="text-sm font-medium text-zinc-700">Name on card</span>
        <input v-model.trim="form.cardholderName" autocomplete="cc-name" class="mt-1 block h-11 w-full rounded-lg border-zinc-300 text-sm shadow-sm focus:border-[#2D8CFF] focus:ring-[#2D8CFF]">
        <span v-if="fieldErrors.cardholderName" class="mt-1 block text-xs text-red-600">{{ fieldErrors.cardholderName }}</span>
      </label>

      <div>
        <span class="text-sm font-medium text-zinc-700">Card number</span>
        <div class="relative mt-1 h-11 overflow-hidden rounded-lg border border-zinc-300 bg-white px-3 py-3 shadow-sm focus-within:border-[#2D8CFF] focus-within:ring-1 focus-within:ring-[#2D8CFF]">
          <div ref="numberMount" class="h-full w-full" />
          <div v-if="loading" class="absolute inset-0 flex items-center bg-white px-3 text-sm text-zinc-400">
            <span class="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-zinc-200 border-t-[#2D8CFF]" />
            Loading secure field…
          </div>
        </div>
      </div>

      <div class="grid grid-cols-2 gap-4">
        <label>
          <span class="text-sm font-medium text-zinc-700">Expiration</span>
          <div class="mt-1 grid grid-cols-2 gap-2">
            <input v-model.trim="form.expiryMonth" inputmode="numeric" maxlength="2" autocomplete="cc-exp-month" placeholder="MM" class="block h-11 w-full rounded-lg border-zinc-300 text-sm shadow-sm focus:border-[#2D8CFF] focus:ring-[#2D8CFF]">
            <input v-model.trim="form.expiryYear" inputmode="numeric" maxlength="4" autocomplete="cc-exp-year" placeholder="YYYY" class="block h-11 w-full rounded-lg border-zinc-300 text-sm shadow-sm focus:border-[#2D8CFF] focus:ring-[#2D8CFF]">
          </div>
          <span v-if="fieldErrors.expiration" class="mt-1 block text-xs text-red-600">{{ fieldErrors.expiration }}</span>
        </label>
        <div>
          <span class="text-sm font-medium text-zinc-700">CVV</span>
          <div class="relative mt-1 h-11 overflow-hidden rounded-lg border border-zinc-300 bg-white px-3 py-3 shadow-sm focus-within:border-[#2D8CFF] focus-within:ring-1 focus-within:ring-[#2D8CFF]">
            <div ref="cvvMount" class="h-full w-full" />
            <div v-if="loading" class="absolute inset-0 flex items-center bg-white px-3 text-sm text-zinc-400">Loading…</div>
          </div>
        </div>
      </div>

      <div class="border-t border-zinc-200 pt-4"><p class="text-sm font-semibold text-zinc-900">Billing address</p></div>
      <label class="block">
        <span class="text-sm font-medium text-zinc-700">Street address</span>
        <input v-model.trim="form.address1" autocomplete="billing address-line1" class="mt-1 block h-11 w-full rounded-lg border-zinc-300 text-sm shadow-sm focus:border-[#2D8CFF] focus:ring-[#2D8CFF]">
        <span v-if="fieldErrors.address1" class="mt-1 block text-xs text-red-600">{{ fieldErrors.address1 }}</span>
      </label>
      <div class="grid gap-4 sm:grid-cols-2">
        <label>
          <span class="text-sm font-medium text-zinc-700">City</span>
          <input v-model.trim="form.locality" autocomplete="billing address-level2" class="mt-1 block h-11 w-full rounded-lg border-zinc-300 text-sm shadow-sm focus:border-[#2D8CFF] focus:ring-[#2D8CFF]">
          <span v-if="fieldErrors.locality" class="mt-1 block text-xs text-red-600">{{ fieldErrors.locality }}</span>
        </label>
        <label>
          <span class="text-sm font-medium text-zinc-700">State</span>
          <input v-model.trim="form.administrativeArea" autocomplete="billing address-level1" class="mt-1 block h-11 w-full rounded-lg border-zinc-300 text-sm shadow-sm focus:border-[#2D8CFF] focus:ring-[#2D8CFF]">
          <span v-if="fieldErrors.administrativeArea" class="mt-1 block text-xs text-red-600">{{ fieldErrors.administrativeArea }}</span>
        </label>
      </div>
      <div class="grid gap-4 sm:grid-cols-2">
        <label>
          <span class="text-sm font-medium text-zinc-700">Postal code</span>
          <input v-model.trim="form.postalCode" autocomplete="billing postal-code" class="mt-1 block h-11 w-full rounded-lg border-zinc-300 text-sm shadow-sm focus:border-[#2D8CFF] focus:ring-[#2D8CFF]">
          <span v-if="fieldErrors.postalCode" class="mt-1 block text-xs text-red-600">{{ fieldErrors.postalCode }}</span>
        </label>
        <label>
          <span class="text-sm font-medium text-zinc-700">Country</span>
          <input v-model.trim="form.country" maxlength="2" autocomplete="billing country" class="mt-1 block h-11 w-full rounded-lg border-zinc-300 text-sm uppercase shadow-sm focus:border-[#2D8CFF] focus:ring-[#2D8CFF]">
          <span v-if="fieldErrors.country" class="mt-1 block text-xs text-red-600">{{ fieldErrors.country }}</span>
        </label>
      </div>

      <p v-if="errorMessage" aria-live="polite" class="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{{ errorMessage }}</p>
      <p class="text-xs leading-5 text-zinc-500">By saving this card, you authorize Platforma to use it for your current plan, upgrades, and recurring renewals until canceled.</p>
    </div>

    <div class="mt-7 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
      <button type="button" class="rounded-lg border border-zinc-300 px-4 py-2.5 text-sm font-semibold text-zinc-700 transition hover:bg-zinc-50" :disabled="submitting" @click="$emit('close')">Cancel</button>
      <button type="button" class="rounded-lg bg-[#2D8CFF] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#1f78df] disabled:cursor-not-allowed disabled:opacity-60" :disabled="submitting || !ready" @click="save">
        {{ submitting ? 'Saving…' : (loading ? 'Loading secure form…' : 'Save payment method') }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { nextTick, onBeforeUnmount, onMounted, reactive, ref } from 'vue';
import { XMarkIcon } from '@heroicons/vue/24/outline';

const emit = defineEmits(['close', 'saved']);
const apiBase = `${process.env.VUE_APP_API_PROXY_URL || ''}/api/platforma-creator-billing`;
const numberMount = ref(null);
const cvvMount = ref(null);
const ready = ref(false);
const loading = ref(true);
const submitting = ref(false);
const errorMessage = ref('');
const form = reactive({ cardholderName: '', expiryMonth: '', expiryYear: '', address1: '', locality: '', administrativeArea: '', postalCode: '', country: 'US' });
const fieldErrors = reactive({ cardholderName: '', expiration: '', address1: '', locality: '', administrativeArea: '', postalCode: '', country: '' });
let captureContext = '';
let microform = null;
let numberField = null;
let cvvField = null;
let detectedCardType = '';
let defaultBillingDetails = {};

const requestJson = async (path, options = {}) => {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 15000);
  try {
    const response = await fetch(`${apiBase}${path}`, {
      credentials: 'include',
      cache: 'no-store',
      ...options,
      signal: controller.signal,
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || payload?.success === false) throw new Error(payload?.message || 'Billing request failed');
    return payload;
  } catch (error) {
    if (error?.name === 'AbortError') throw new Error('The billing service took too long to respond. Please try again.');
    throw error;
  } finally {
    window.clearTimeout(timeout);
  }
};

const loadScript = (src, integrity) => new Promise((resolve, reject) => {
  if (!src || !integrity) return reject(new Error('Secure card setup is unavailable. Please try again.'));
  const assetKey = `${src}|${integrity}`;
  const currentScript = document.querySelector('script[data-platforma-boa-microform="true"]');
  if (window.Flex && window.__platformaBoaFlexAssetKey === assetKey) return resolve(window.Flex);
  if (currentScript) currentScript.remove();
  try { delete window.Flex; } catch (_error) { window.Flex = undefined; }
  const script = document.createElement('script');
  script.src = src;
  script.async = true;
  script.integrity = integrity;
  script.crossOrigin = 'anonymous';
  script.dataset.platformaBoaMicroform = 'true';
  const fail = () => {
    script.remove();
    reject(new Error('Secure card fields could not be loaded. Close this window and try again.'));
  };
  script.onload = () => {
    if (!window.Flex) return fail();
    window.__platformaBoaFlexAssetKey = assetKey;
    resolve(window.Flex);
  };
  script.onerror = fail;
  document.head.appendChild(script);
});

const loadSecureField = (field, mount, label) => new Promise((resolve, reject) => {
  if (!field || !mount) return reject(new Error(`${label} could not be initialized.`));
  const timeout = window.setTimeout(() => {
    reject(new Error('The secure card fields took too long to load. Close this window and try again.'));
  }, 12000);
  const cleanup = () => {
    window.clearTimeout(timeout);
    field.off?.('load', onLoad);
    field.off?.('error', onError);
  };
  const onLoad = () => { cleanup(); resolve(); };
  const onError = () => { cleanup(); reject(new Error(`${label} could not be loaded. Close this window and try again.`)); };
  field.on('load', onLoad);
  field.on('error', onError);
  try { field.load(mount); } catch (_error) { onError(); }
});

const destroy = () => {
  [numberField, cvvField].forEach((field) => {
    try { field?.unload?.(); } catch (_error) { /* already unloaded */ }
    try { field?.dispose?.(); } catch (_error) { /* already disposed */ }
  });
  numberField = null;
  cvvField = null;
  microform = null;
  detectedCardType = '';
  captureContext = '';
  defaultBillingDetails = {};
  ready.value = false;
};

const initialize = async () => {
  destroy();
  loading.value = true;
  errorMessage.value = '';
  try {
    const session = await requestJson('/bank-of-america/microform/capture-context', { method: 'POST' });
    if (session.targetOrigin !== window.location.origin) {
      throw new Error(`Secure card entry is not configured for ${window.location.origin}.`);
    }
    const Flex = await loadScript(session.clientLibraryUrl, session.clientLibraryIntegrity);
    if (!Flex || !session.captureContext) {
      throw new Error('Secure card setup is unavailable. Please try again.');
    }
    await nextTick();
    captureContext = session.captureContext;
    defaultBillingDetails = session.defaultBillingDetails || {};
    const flex = new Flex(captureContext);
    microform = flex.microform({
      styles: {
        input: { color: '#18181b', 'font-size': '16px', 'font-family': 'Arial, sans-serif' },
        '::placeholder': { color: '#a1a1aa' },
        ':focus': { color: '#18181b' },
        invalid: { color: '#dc2626' },
      },
    });
    numberField = microform.createField('number', { placeholder: 'Card number', 'aria-label': 'Card number' });
    cvvField = microform.createField('securityCode', { placeholder: 'CVV', 'aria-label': 'Card security code' });
    numberField.on('change', (data = {}) => {
      const candidates = Array.isArray(data.card) ? data.card : [];
      const recognizedTypes = candidates
        .map((card) => String(card?.cybsCardType || ''))
        .filter((type) => /^\d{3}$/.test(type));
      detectedCardType = data.valid === true && recognizedTypes.length === 1
        ? recognizedTypes[0]
        : '';
    });
    await Promise.all([
      loadSecureField(numberField, numberMount.value, 'Card number'),
      loadSecureField(cvvField, cvvMount.value, 'Security code'),
    ]);
    ready.value = true;
  } catch (error) {
    destroy();
    errorMessage.value = error?.message || 'Secure card input could not be loaded.';
  } finally {
    loading.value = false;
  }
};

const clearFieldErrors = () => Object.keys(fieldErrors).forEach((key) => { fieldErrors[key] = ''; });

const validateForm = () => {
  clearFieldErrors();
  const names = form.cardholderName.split(/\s+/).filter(Boolean);
  if (names.length < 2) fieldErrors.cardholderName = 'Enter the first and last name shown on the card.';
  const month = String(form.expiryMonth || '').padStart(2, '0');
  const year = String(form.expiryYear || '');
  if (!/^(0[1-9]|1[0-2])$/.test(month) || !/^20\d{2}$/.test(year)) fieldErrors.expiration = 'Enter a valid month and four-digit year.';
  if (!form.address1) fieldErrors.address1 = 'Enter the billing street address.';
  if (!form.locality) fieldErrors.locality = 'Enter the billing city.';
  if (!form.administrativeArea) fieldErrors.administrativeArea = 'Enter the billing state.';
  if (!form.postalCode) fieldErrors.postalCode = 'Enter the billing postal code.';
  if (!/^[A-Za-z]{2}$/.test(form.country)) fieldErrors.country = 'Use a two-letter country code.';
  return { valid: !Object.values(fieldErrors).some(Boolean), names, month, year };
};

const normalizedTokenError = (input) => {
  let source = input;
  if (typeof source === 'string') {
    try { source = JSON.parse(source); } catch (_error) { source = { message: source }; }
  }
  if (!source || typeof source !== 'object') source = {};
  const candidates = [source, source.error, source.response, source.data, source.details?.[0]].filter((item) => item && typeof item === 'object');
  const firstValue = (...keys) => {
    for (const candidate of candidates) {
      for (const key of keys) {
        if (candidate[key] != null && String(candidate[key]).trim()) return String(candidate[key]).trim();
      }
    }
    return '';
  };
  const detailSource = candidates.find((candidate) => Array.isArray(candidate.details))?.details || [];
  return {
    reason: firstValue('reason', 'code', 'errorCode'),
    message: firstValue('message', 'description', 'errorMessage'),
    correlationID: firstValue('correlationID', 'correlationId', 'requestId'),
    statusCode: firstValue('statusCode', 'status', 'httpStatus'),
    informationLink: firstValue('informationLink'),
    details: detailSource.slice(0, 5).map((detail) => ({
      reason: String(detail?.reason || detail?.code || '').slice(0, 120),
      message: String(detail?.message || detail?.description || '').slice(0, 300),
      location: String(detail?.location || detail?.field || '').slice(0, 120),
    })),
    raw: input,
  };
};

const tokenErrorMessage = (error = {}) => {
  const reference = String(error.correlationID || '').trim();
  const suffix = reference ? ` Reference: ${reference}` : '';
  switch (String(error.reason || '')) {
    case 'CREATE_TOKEN_VALIDATION_FIELDS':
      return 'Check the card number and CVV, then try again.';
    case 'CREATE_TOKEN_VALIDATION_PARAMS':
      return 'Check the expiration month and year, then try again.';
    case 'CAPTURE_CONTEXT_EXPIRED':
    case 'CREATE_TOKEN_TIMEOUT':
      return 'This secure card session expired. Close this window and try again.';
    case 'CREATE_TOKEN_XHR_ERROR':
      return `The secure payment service could not be reached. Close this window and try again.${suffix}`;
    case 'CREATE_TOKEN_VALIDATION_SERVERSIDE':
      return `The payment service rejected the secure card session before a charge was attempted.${suffix}`;
    case 'CREATE_TOKEN_PLATFORM_ERROR':
      return `The secure payment service could not create a card token. No charge was attempted.${suffix}`;
    default:
      return error.message
        ? `The secure card could not be verified: ${error.message}${suffix}`
        : `The secure card could not be verified. No charge was attempted.${suffix}`;
  }
};

const reportTokenizationError = (error) => {
  void fetch(`${apiBase}/bank-of-america/microform/client-error`, {
    method: 'POST',
    credentials: 'include',
    cache: 'no-store',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      reason: error.reason,
      message: error.message,
      correlationId: error.correlationID,
      statusCode: error.statusCode,
      informationLink: error.informationLink,
      details: error.details,
      browserOrigin: window.location.origin,
    }),
  }).catch(() => {});
};

const createTokenAttempt = (month, year, cardType) => new Promise((resolve, reject) => {
  const options = { expirationMonth: month, expirationYear: year };
  if (/^\d{3}$/.test(cardType)) options.type = cardType;
  let settled = false;
  const timeout = window.setTimeout(() => {
    settled = true;
    const tokenError = new Error(tokenErrorMessage({ reason: 'CREATE_TOKEN_TIMEOUT' }));
    tokenError.tokenizationError = { reason: 'CREATE_TOKEN_TIMEOUT' };
    reject(tokenError);
  }, 12000);
  const handleResult = (error, token) => {
    if (settled) return;
    settled = true;
    window.clearTimeout(timeout);
    if (error) {
      const normalized = normalizedTokenError(error);
      const tokenError = new Error(tokenErrorMessage(normalized));
      tokenError.tokenizationError = normalized;
      return reject(tokenError);
    }
    resolve(token);
  };
  try {
    microform.createToken(options, handleResult);
  } catch (error) {
    handleResult(error);
  }
});

const createToken = async (month, year, cardType) => {
  let failure;
  try {
    return await createTokenAttempt(month, year, cardType);
  } catch (error) {
    failure = error;
  }
  if (failure?.tokenizationError?.reason === 'CREATE_TOKEN_PLATFORM_ERROR') {
    try {
      return await createTokenAttempt(month, year, cardType);
    } catch (retryError) {
      failure = retryError;
    }
  }
  const normalized = failure?.tokenizationError || normalizedTokenError(failure);
  console.warn('[Platforma billing] Secure card tokenization failed', {
    reason: normalized.reason,
    message: normalized.message,
    correlationID: normalized.correlationID,
    statusCode: normalized.statusCode,
    informationLink: normalized.informationLink,
    details: normalized.details,
    rawError: normalized.raw,
  });
  reportTokenizationError(normalized);
  throw new Error(tokenErrorMessage(normalized));
};

const save = async () => {
  if (!ready.value || submitting.value) return;
  errorMessage.value = '';
  const validation = validateForm();
  if (!validation.valid) {
    errorMessage.value = 'Please complete the highlighted billing details.';
    return;
  }
  submitting.value = true;
  try {
    if (!defaultBillingDetails.email) throw new Error('Your creator account needs a billing email before a card can be saved.');
    const transientToken = await createToken(validation.month, validation.year, detectedCardType);
    const payload = await requestJson('/bank-of-america/microform/complete', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        captureContext,
        transientToken,
        storedCredentialConsent: true,
        billingDetails: {
          firstName: validation.names[0],
          lastName: validation.names.slice(1).join(' '),
          email: defaultBillingDetails.email,
          address1: form.address1,
          locality: form.locality,
          administrativeArea: form.administrativeArea,
          postalCode: form.postalCode,
          country: form.country.toUpperCase(),
        },
      }),
    });
    emit('saved', payload);
  } catch (error) {
    errorMessage.value = error?.message || 'The payment method could not be saved. Please try again.';
  } finally {
    submitting.value = false;
  }
};

onMounted(() => { void initialize(); });
onBeforeUnmount(() => {
  destroy();
});
</script>
